import { useEffect, useRef, useState } from 'react';
import { Bounds, OrbitControls } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { DoubleSide, Matrix4, Mesh, type BufferGeometry } from 'three';

import { Spinner } from '@shared';

import {
  getStlLayerColor,
  getSupportedViewerFileFormat,
  layoutStlLayers,
  STL_LAYOUT_CONFIG,
  type StlLayerColor,
  type StlLayoutConfig,
} from './stl-layout';

type StlVector3 = [number, number, number];

export interface StlViewerLayer {
  id: string;
  name: string;
  file: Blob;
  color?: StlLayerColor;
}

interface StlViewerProps {
  files: readonly StlViewerLayer[];
  ariaLabel: string;
  loadingLabel: string;
  errorLabel: string;
  backgroundColor?: string;
  modelColor?: string;
  scanColor?: string;
  aestheticPlateColor?: string;
  modelRotation?: StlVector3;
  layerOpacities?: Readonly<Record<string, number>>;
  visibleLayerIds?: readonly string[];
  layoutConfig?: StlLayoutConfig;
}

interface ParsedLayer {
  id: string;
  name: string;
  geometries: BufferGeometry[];
  color?: StlLayerColor;
}

type ParseResult =
  { layerKey: string; layers: ParsedLayer[]; status: 'ready' } | { layerKey: string; status: 'error' };

async function parseViewerFile(file: StlViewerLayer): Promise<BufferGeometry[]> {
  const format = getSupportedViewerFileFormat(file.name);
  if (format === undefined) {
    throw new Error('Unsupported 3D file format.');
  }

  switch (format) {
    case 'stl':
      return [new STLLoader().parse(await file.file.arrayBuffer())];
    case 'ply':
      return [new PLYLoader().parse(await file.file.arrayBuffer())];
    case 'obj': {
      const object = new OBJLoader().parse(await file.file.text());
      const geometries: BufferGeometry[] = [];
      object.traverse((child) => {
        if (child instanceof Mesh) {
          geometries.push(child.geometry);
        }
      });
      return geometries;
    }
  }
}

function readThemeColor(variableName: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim() || null;
}

export function normalizeStlLayerOpacity(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 1;
  }

  return Math.min(1, Math.max(0, value));
}

function StlModel({
  layers,
  modelColor,
  scanColor,
  aestheticPlateColor,
  modelRotation,
  layerOpacities,
  visibleLayerIds,
  layoutConfig,
}: {
  layers: ParsedLayer[];
  modelColor: string;
  scanColor: string;
  aestheticPlateColor: string;
  modelRotation: StlVector3;
  layerOpacities: Readonly<Record<string, number>>;
  visibleLayerIds: readonly string[] | undefined;
  layoutConfig: StlLayoutConfig;
}) {
  // layoutStlLayers supplies only per-file CAD transforms; the outer group is user-controlled.
  const layerTransforms = layoutStlLayers(layers, layoutConfig);

  return (
    <Bounds fit clip margin={1.25}>
      <group rotation={modelRotation}>
        {layers.map((layer) => {
          const transform = layerTransforms.find((layerTransform) => layerTransform.id === layer.id);
          if (!transform) {
            return null;
          }
          const color = getStlLayerColor(layer.name, modelColor, scanColor, aestheticPlateColor, layer.color);
          const opacity = normalizeStlLayerOpacity(layerOpacities[layer.id]);
          const isVisible = visibleLayerIds === undefined || visibleLayerIds.includes(layer.id);

          return (
            <group
              key={layer.id}
              matrix={new Matrix4().fromArray(transform.matrix)}
              matrixAutoUpdate={false}
              visible={isVisible}
            >
              {layer.geometries.map((geometry, index) => (
                <mesh key={`${layer.id}-${index}`} geometry={geometry} castShadow receiveShadow>
                  <meshStandardMaterial
                    color={color}
                    roughness={0.46}
                    metalness={0.08}
                    side={DoubleSide}
                    transparent={opacity < 1}
                    opacity={opacity}
                    depthWrite={opacity >= 1}
                  />
                </mesh>
              ))}
            </group>
          );
        })}
      </group>
    </Bounds>
  );
}

export function StlViewer({
  files,
  ariaLabel,
  loadingLabel,
  errorLabel,
  backgroundColor,
  modelColor,
  scanColor,
  aestheticPlateColor,
  modelRotation = [0, 0, 0],
  layerOpacities = {},
  visibleLayerIds,
  layoutConfig = STL_LAYOUT_CONFIG,
}: StlViewerProps) {
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const filesRef = useRef(files);
  // Visibility, color, and transform changes should reuse parsed geometry; only layer IDs key parsing.
  const layerKey = files.map((layer) => layer.id).join('|');

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    let isActive = true;
    const parsedGeometries: BufferGeometry[] = [];
    const filesToParse = filesRef.current;

    // Parse layers together so the viewer never displays a partially loaded set.
    void Promise.all(
      filesToParse.map(async (file) => {
        const geometries = await parseViewerFile(file);
        parsedGeometries.push(...geometries);

        if (geometries.length === 0) {
          throw new Error('The OBJ file contains no meshes.');
        }

        geometries.forEach((geometry) => {
          const position = geometry.getAttribute('position');
          if (!position || position.count === 0) {
            throw new Error('The 3D file contains no geometry.');
          }

          geometry.computeVertexNormals();
        });

        return { id: file.id, name: file.name, geometries };
      }),
    )
      .then((layers) => {
        if (!isActive) {
          // The request may finish after the layer set changes or the viewer unmounts.
          layers.forEach((layer) => layer.geometries.forEach((geometry) => geometry.dispose()));
          return;
        }

        setParseResult({ layerKey, layers, status: 'ready' });
      })
      .catch(() => {
        // A failed member invalidates this batch; release every geometry that already parsed.
        parsedGeometries.forEach((geometry) => geometry.dispose());
        if (isActive) {
          setParseResult({ layerKey, status: 'error' });
        }
      });

    return () => {
      isActive = false;
    };
  }, [layerKey]);

  useEffect(() => {
    return () => {
      if (parseResult?.status === 'ready') {
        // BufferGeometry owns GPU-side resources and must be released when replaced or unmounted.
        parseResult.layers.forEach((layer) => layer.geometries.forEach((geometry) => geometry.dispose()));
      }
    };
  }, [parseResult]);

  const currentResult = parseResult?.layerKey === layerKey ? parseResult : null;
  const resolvedModelColor = modelColor ?? readThemeColor('--md-tooth-color') ?? 'white';
  const resolvedScanColor = scanColor ?? readThemeColor('--md-gum-color') ?? resolvedModelColor;
  const resolvedAestheticPlateColor =
    aestheticPlateColor ?? readThemeColor('--md-aestheticplate-color') ?? resolvedModelColor;
  const resolvedBackgroundColor = backgroundColor ?? readThemeColor('--md-primary') ?? 'black';
  if (!currentResult) {
    return (
      <div className="md-review__viewer-loading grid h-full min-h-[20rem] place-items-center p-8 text-[var(--md-viewer-text)]">
        <Spinner label={loadingLabel} />
      </div>
    );
  }

  if (currentResult.status === 'error') {
    return (
      <div className="grid h-full min-h-[20rem] place-items-center p-8 text-center">
        <p className="m-0 text-sm text-[var(--md-viewer-text)]" role="alert">
          {errorLabel}
        </p>
      </div>
    );
  }

  return (
    <div className="relative h-full min-h-[20rem] w-full">
      <Canvas
        camera={{ position: [0, -5, 0], up: [0, 0, 1], fov: 40 }}
        dpr={[1, 2]}
        gl={{ antialias: true, preserveDrawingBuffer: true }}
        style={{ height: '100%', width: '100%' }}
        onCreated={({ gl, size }) => {
          gl.setSize(size.width, size.height, true);
        }}
        aria-label={ariaLabel}
      >
        <color attach="background" args={[resolvedBackgroundColor]} />
        <ambientLight intensity={1.8} />
        <directionalLight castShadow intensity={2.4} position={[4, -3, 8]} />
        <directionalLight intensity={1.2} position={[-4, 2, -3]} />
        <StlModel
          layers={currentResult.layers.map((layer) => {
            const color = files.find((file) => file.id === layer.id)?.color;
            return { ...layer, ...(color ? { color } : {}) };
          })}
          modelColor={resolvedModelColor}
          scanColor={resolvedScanColor}
          aestheticPlateColor={resolvedAestheticPlateColor}
          modelRotation={modelRotation}
          layerOpacities={layerOpacities}
          visibleLayerIds={visibleLayerIds}
          layoutConfig={layoutConfig}
        />
        <OrbitControls makeDefault enableDamping minDistance={0.1} maxDistance={100000} />
      </Canvas>
      <svg
        className="pointer-events-none absolute bottom-4 left-4 h-16 w-16"
        viewBox="0 0 64 64"
        role="img"
        aria-label="X, Y, Z"
        data-testid="viewer-axis-reference"
      >
        <line x1="24" y1="38" x2="50" y2="38" stroke="var(--md-viewer-axis-x)" strokeWidth="2" />
        <line x1="24" y1="38" x2="8" y2="54" stroke="var(--md-viewer-axis-y)" strokeWidth="2" />
        <line x1="24" y1="38" x2="24" y2="14" stroke="var(--md-viewer-axis-z)" strokeWidth="2" />
        <circle cx="24" cy="38" r="2.5" fill="var(--md-viewer-text)" />
        <text x="56" y="42" fill="var(--md-viewer-axis-x)" fontSize="11" fontWeight="700">
          X
        </text>
        <text x="0" y="64" fill="var(--md-viewer-axis-y)" fontSize="11" fontWeight="700">
          Y
        </text>
        <text x="20" y="9" fill="var(--md-viewer-axis-z)" fontSize="11" fontWeight="700">
          Z
        </text>
      </svg>
    </div>
  );
}
